<template>
  <StepCard
    step-id="connect_payroll"
    :description="t('description')"
    :edit-label="t('edit')"
    :completed
    unskippable
    :errors="serverErrors"
    @cancel="$emit('cancel')"
  >
    <template #action>
      <ZoneButton
        variant="outline"
        color="gray_alt"
        :disabled="saving"
        :tooltip="saving ? t('save_in_progress') : null"
        size-class="px-4 py-2 text-sm font-medium!"
        class="whitespace-nowrap text-gray-700! hover:bg-gray-50!"
        data-testid="payroll-pay-outside"
        @click="save"
      >
        {{ t('pay_outside') }}
      </ZoneButton>
    </template>

    <template #summary>
      <div class="flex flex-col gap-3">
        <div
          v-for="provider in connectedProviders"
          :key="provider.id"
          class="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4"
          :data-testid="`payroll-summary-${provider.id}`"
        >
          <div class="flex min-w-0 items-center gap-3">
            <img
              :src="provider.logo"
              alt=""
              class="size-6 shrink-0 rounded object-contain"
            />
            <div class="min-w-0">
              <p class="text-sm font-medium text-gray-900">{{ t(`providers.${provider.id}.name`) }}</p>
              <p class="text-xs text-gray-500">{{ t(`providers.${provider.id}.hint`) }}</p>
            </div>
          </div>
          <span class="inline-flex shrink-0 select-none items-center gap-1 whitespace-nowrap rounded-full bg-green-100 px-2 py-1 text-xs font-regular text-green-800">
            <ZoneIcon
              icon="check_circle"
              class="text-sm!"
              aria-hidden="true"
            />
            {{ t('connected') }}
          </span>
        </div>
        <p
          v-if="!connectedProviders.length"
          class="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700"
          data-testid="payroll-summary-outside"
        >
          {{ t('outside_summary') }}
        </p>
      </div>
    </template>

    <div class="flex flex-col gap-3">
      <div
        v-for="provider in providers"
        :key="provider.id"
        class="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        :data-testid="`payroll-provider-${provider.id}`"
      >
        <div class="flex min-w-0 items-center gap-3">
          <img
            :src="provider.logo"
            alt=""
            class="size-6 shrink-0 rounded object-contain"
          />
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <p class="text-sm font-medium text-gray-900">{{ t(`providers.${provider.id}.name`) }}</p>
              <ZonePill
                v-if="provider.id === 'wise'"
                color="purple"
                size="md"
              >
                {{ t('recommended') }}
              </ZonePill>
            </div>
            <p class="text-xs text-gray-500">{{ t(`providers.${provider.id}.hint`) }}</p>
          </div>
        </div>
        <ZoneButton
          color="primary_alt"
          size-class="h-9 px-4 text-sm font-medium!"
          class="shrink-0"
          :href="provider.connected ? null : provider.connectUrl"
          :disabled="provider.connected || !provider.connectUrl"
          :tooltip="connectHint(provider)"
          target="_blank"
          rel="noopener"
          :data-testid="`payroll-connect-${provider.id}`"
        >
          {{ provider.connected ? t('connected') : t('connect') }}
        </ZoneButton>
      </div>
    </div>
  </StepCard>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StepCard from '~/components/experiments/0239_getting_started_checklist/StepCard.vue';
import { useStepSave } from '~/components/experiments/0239_getting_started_checklist/useStepSave';
import ZoneButton from 'zone/components/base/Button.vue';
import ZoneIcon from 'zone/components/base/Icon.vue';
import ZonePill from 'zone/components/base/Pill.vue';
import { AR0239_LOGO_BITWAGE, AR0239_LOGO_PAYONEER, AR0239_LOGO_PAYPAL, AR0239_LOGO_WISE } from '~/rails_assets';

const LOGOS = { wise: AR0239_LOGO_WISE, payoneer: AR0239_LOGO_PAYONEER, bitwage: AR0239_LOGO_BITWAGE, paypal: AR0239_LOGO_PAYPAL };

const { data, completed, organization } = defineProps({
  data: { type: Object, required: true },
  completed: { type: Boolean },
  organization: { type: Object, required: true },
});

const emit = defineEmits(['saved', 'cancel']);

const { t } = useI18n();
const { saving, serverErrors, finishStep, run } = useStepSave(emit);

const save = () => run(() => finishStep(organization.id, 'connect_payroll', { abortReload: true }));

const providers = computed(() => Object.entries(LOGOS).map(([id, logo]) => ({
  id,
  logo,
  connectUrl: data[id]?.connect_url ?? null,
  connected: !!data[id]?.enabled,
})));
const connectHint = (provider) => {
  if (provider.connected) {return t('connected_hint');}

  return provider.connectUrl ? null : t('unavailable_hint');
};
const connectedProviders = computed(() => providers.value.filter((provider) => provider.connected));
</script>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "description": "Pay your team based on tracked hours.",
    "providers": {
      "wise": { "name": "Wise", "hint": "Pay team members automatically or manually through Wise." },
      "payoneer": { "name": "Payoneer", "hint": "Send automatic or manual payments to team members via Payoneer." },
      "bitwage": { "name": "Bitwage", "hint": "Pay team members via automatic credit card payments through Bitwage." },
      "paypal": { "name": "PayPal", "hint": "Pay your team automatically or manually through PayPal." }
    },
    "recommended": "Recommended",
    "connect": "Connect",
    "connected": "Connected",
    "connected_hint": "Already connected to your organization.",
    "unavailable_hint": "This provider is not available for your organization.",
    "pay_outside": "I pay outside Hubstaff",
    "outside_summary": "You pay your team outside Hubstaff.",
    "edit": "Add another payroll"
  }
}
</i18n>
